// NotFoundPage — generic 404 dispatcher.
//
// Two entry paths per FRONTEND-PATTERNS §Resolver chain
// (+ lib/apiClient.ts §404 branch):
//
//   (1) apiClient interceptor 404 branch — backend returned a typed
//       *_NOT_FOUND error code. The interceptor navigates here with
//       { state: { errorCode } } (wired through React Router via
//       setNavigate).
//
//   (2) catch-all route — user typed a URL the router doesn't match.
//       location.state is empty; we render the generic copy.
//
// Per-code messages live in errors.json (the central code→string mapping
// the apiClient already uses). The page chooses between those known codes
// and a generic default. Stays in the pre-auth zone (no ProtectedRoute) so
// unauthenticated 404s don't loop through /login.

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { LogoStacked } from '../components/ui/Logo.js';
import { Link } from '../components/ui/Link.js';
import { useDocumentTitle } from '../hooks/useDocumentTitle.js';

interface LocationState {
  errorCode?: string;
}

const KNOWN_CODES = new Set([
  'PROJECT_NOT_FOUND',
  'PROJECT_SUSPENDED',
  'PROJECT_TERMINATED',
  'RESOURCE_NOT_FOUND',
]);

export function NotFoundPage() {
  const { t } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const location = useLocation();
  const state = location.state as LocationState | null;
  const code = state?.errorCode;

  // Resolved message: typed-error backend code → errors.json mapping;
  // unknown / absent → generic "resource not found" copy.
  const heading = t('notFound.title');
  useDocumentTitle(heading);
  const message = code && KNOWN_CODES.has(code) ? tErrors(code) : t('notFound.body');

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-[var(--color-bg-page)]">
      <div className="w-full max-w-sm space-y-8">
        <div className="flex justify-center text-[var(--color-text-primary)]">
          <LogoStacked height={88} />
        </div>
        <div className="space-y-4 text-center">
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">{heading}</h1>
          <p className="text-sm text-[var(--color-text-secondary)]">{message}</p>
          <div>
            <Link to="/">{t('notFound.goHome')}</Link>
          </div>
        </div>
      </div>
    </main>
  );
}

export default NotFoundPage;
